/**
 * The ONE place that knows what a user-content image URL looks like and how to make
 * it loadable on web. Profile photos, bills and stock images now live in the
 * merchant's Google Drive (Android writes `https://drive.google.com/uc?export=view&id=…`),
 * and a Drive URL can't be used as an `<img src>` reliably — it has to go through our
 * same-origin proxy. Legacy `storage.googleapis.com/okdoc-…` URLs still show up while
 * Android migrates them.
 *
 * If Drive (or Android) changes shape again, change it HERE — `AppImage`,
 * `useImageBlobUrl` and the server proxy all resolve through this file.
 *
 * Pure (no DOM / no app imports): the express server imports it too.
 */

export const IMAGE_PROXY_PATH = '/api/proxy/image'

const DRIVE_HOSTS = ['drive.google.com', 'docs.google.com', 'drive.usercontent.google.com']
const LEGACY_STORAGE_HOST = 'storage.googleapis.com'

/** Hosts the server proxy may fetch from. Anything else is refused (no open proxy). */
export const IMAGE_PROXY_ALLOWED_HOSTS = [
  ...DRIVE_HOSTS,
  LEGACY_STORAGE_HOST,
  'lh3.googleusercontent.com'
]

// Thumbnail widths we ask Drive for — a few buckets so the proxy/browser cache is shared.
const THUMBNAIL_WIDTHS = [64, 128, 256, 512, 1024, 2048]

const parseUrl = (url?: string | null): URL | null => {
  if (!url) {
    return null
  }
  try {
    // Tolerate an HTML-escaped separator (`&amp;id=`) in a stored URL.
    return new URL(url.replace(/&amp;/g, '&'))
  } catch (e) {
    return null
  }
}

/** Drive file id from any Drive URL shape (`uc?id=`, `open?id=`, `thumbnail?id=`, `file/d/<id>/…`). */
export const getDriveFileId = (url?: string | null): string | null => {
  const parsed = parseUrl(url)
  if (!parsed || !DRIVE_HOSTS.includes(parsed.hostname)) {
    return null
  }
  const fromPath = parsed.pathname.match(/\/d\/([\w-]+)/)
  const id = parsed.searchParams.get('id') || (fromPath ? fromPath[1] : null)
  return id && /^[\w-]+$/.test(id) ? id : null
}

export const isDriveImage = (url?: string | null): boolean => getDriveFileId(url) !== null

const canonicalDriveUrl = (fileId: string) => `https://drive.google.com/uc?export=view&id=${fileId}`

/** Smallest bucket that still covers `cssWidth` on a 2x screen. */
export const toThumbnailWidth = (cssWidth?: number): number | undefined => {
  if (!cssWidth || !Number.isFinite(cssWidth) || cssWidth <= 0) {
    return undefined
  }
  const wanted = Math.ceil(cssWidth * 2)
  return THUMBNAIL_WIDTHS.find((w) => w >= wanted) || THUMBNAIL_WIDTHS[THUMBNAIL_WIDTHS.length - 1]
}

const proxied = (url: string, width?: number) =>
  `${IMAGE_PROXY_PATH}?url=${encodeURIComponent(url)}${width ? `&w=${width}` : ''}`

/**
 * URL to put in an `<img src>` (or hand to a library avatar). Drive images go through
 * the proxy — pass `width` (the rendered CSS width in px) to load a small thumbnail
 * instead of the full photo. Everything else is returned untouched.
 */
export const resolveImageUrl = (url?: string | null, options: { width?: number } = {}): string => {
  if (!url) {
    return ''
  }
  const driveFileId = getDriveFileId(url)
  if (driveFileId) {
    return proxied(canonicalDriveUrl(driveFileId), toThumbnailWidth(options.width))
  }
  return url
}

/**
 * URL for `fetch()` (blob / PDF / canvas use). Same as `resolveImageUrl`, but the legacy
 * storage bucket is proxied too — it serves no CORS headers, which an `<img>` doesn't
 * need but a `fetch` does.
 */
export const resolveFetchableImageUrl = (url?: string | null): string => {
  if (!url) {
    return ''
  }
  const parsed = parseUrl(url)
  if (parsed && parsed.hostname === LEGACY_STORAGE_HOST) {
    return proxied(url)
  }
  return resolveImageUrl(url)
}

/**
 * SERVER side of the proxy: the upstream URLs to try, in order, for a requested image —
 * or `null` when the URL isn't one we proxy. `width` is the `w` query param.
 */
export const getImageProxyUpstreams = (url?: string | null, width?: number): string[] | null => {
  const parsed = parseUrl(url)
  if (
    !parsed ||
    parsed.protocol !== 'https:' ||
    !IMAGE_PROXY_ALLOWED_HOSTS.includes(parsed.hostname)
  ) {
    return null
  }
  const driveFileId = getDriveFileId(url)
  if (!driveFileId) {
    // A Drive host without a recognisable file id has nothing we can fetch.
    return DRIVE_HOSTS.includes(parsed.hostname) ? null : [parsed.toString()]
  }
  const download = `https://drive.usercontent.google.com/download?id=${driveFileId}&export=view`
  const thumbnail = (w: number) => `https://drive.google.com/thumbnail?id=${driveFileId}&sz=w${w}`
  const safeWidth =
    width && Number.isFinite(width) ? Math.min(Math.max(Math.round(width), 16), 2048) : undefined
  // Sized request → thumbnail first (small + fast); full size → the original first.
  return safeWidth ? [thumbnail(safeWidth), download] : [download, thumbnail(2048)]
}
